import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth, API } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Plus, FolderKanban, ArrowRight, MoreVertical, Trash2, Edit, MapPin, Check, ChevronsUpDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Breadcrumb from '../components/Breadcrumb';

// US Cities with states for autocomplete
const US_CITIES = [
  "New York, NY", "Los Angeles, CA", "Chicago, IL", "Houston, TX", "Phoenix, AZ",
  "Philadelphia, PA", "San Antonio, TX", "San Diego, CA", "Dallas, TX", "San Jose, CA",
  "Austin, TX", "Jacksonville, FL", "Fort Worth, TX", "Columbus, OH", "Charlotte, NC",
  "San Francisco, CA", "Indianapolis, IN", "Seattle, WA", "Denver, CO", "Boston, MA",
  "Nashville, TN", "Detroit, MI", "Portland, OR", "Las Vegas, NV", "Memphis, TN",
  "Louisville, KY", "Baltimore, MD", "Milwaukee, WI", "Albuquerque, NM", "Tucson, AZ",
  "Fresno, CA", "Sacramento, CA", "Atlanta, GA", "Miami, FL", "Oakland, CA",
  "Minneapolis, MN", "Cleveland, OH", "Raleigh, NC", "Omaha, NE", "Virginia Beach, VA",
  "Colorado Springs, CO", "Tampa, FL", "Honolulu, HI", "Arlington, TX", "New Orleans, LA",
  "Santa Ana, CA", "St. Louis, MO", "Pittsburgh, PA", "Cincinnati, OH", "Orlando, FL",
  "Irvine, CA", "Scottsdale, AZ", "Boca Raton, FL", "Palm Beach, FL", "Beverly Hills, CA",
  "Aspen, CO", "Greenwich, CT", "Hamptons, NY", "Malibu, CA", "Newport Beach, CA",
  "La Jolla, CA", "Sarasota, FL", "Naples, FL", "Charleston, SC", "Savannah, GA",
  "Santa Barbara, CA", "Carmel, CA", "Napa, CA", "Sedona, AZ", "Park City, UT",
  "Vail, CO", "Jackson Hole, WY", "Montauk, NY", "Martha's Vineyard, MA", "Nantucket, MA"
];

const Projects = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(location.state?.openCreate || false);
  const [editProject, setEditProject] = useState(null);
  const [cityOpen, setCityOpen] = useState(false);
  const [citySearch, setCitySearch] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    project_name: '',
    notes: '',
    city: '',
    cover_image: ''
  });

  // Filter cities based on search
  const filteredCities = useMemo(() => {
    if (!citySearch) return US_CITIES.slice(0, 20);
    return US_CITIES.filter(city => 
      city.toLowerCase().includes(citySearch.toLowerCase())
    ).slice(0, 20);
  }, [citySearch]);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/boards`, { withCredentials: true });
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const createProject = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/boards`, formData, { withCredentials: true });
      setProjects([response.data, ...projects]);
      setShowCreate(false);
      setFormData({ name: '', project_name: '', notes: '', city: '', cover_image: '' });
      toast.success('Project created!');
      navigate(`/projects/${response.data.board_id}`);
    } catch (error) {
      toast.error('Failed to create project');
    }
  };

  const updateProject = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/boards/${editProject.board_id}`, formData, { withCredentials: true });
      setProjects(projects.map(p => p.board_id === editProject.board_id ? { ...p, ...formData } : p));
      setEditProject(null);
      setFormData({ name: '', project_name: '', notes: '', city: '', cover_image: '' });
      toast.success('Project updated!');
    } catch (error) {
      toast.error('Failed to update project');
    }
  };

  const deleteProject = async (projectId) => {
    if (!window.confirm('Delete this project? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/boards/${projectId}`, { withCredentials: true });
      setProjects(projects.filter(p => p.board_id !== projectId));
      toast.success('Project deleted');
    } catch (error) {
      toast.error('Failed to delete project');
    }
  };

  const openEdit = (project) => {
    setEditProject(project);
    setFormData({ 
      name: project.name, 
      project_name: project.project_name || '', 
      notes: project.notes || '',
      city: project.city || '',
      cover_image: project.cover_image || ''
    });
  };

  // Get cover image
  const getProjectImage = (project) => {
    if (project.cover_image) return project.cover_image;
    if (project.items?.length > 0 && project.items[0].material?.images?.[0]) {
      return project.items[0].material.images[0];
    }
    return null;
  };

  const ProjectFormFields = ({ isEdit = false }) => (
    <>
      <div>
        <Label className="caption-text mb-2 block">Project Name *</Label>
        <Input
          value={formData.name}
          onChange={(e) => setFormData({...formData, name: e.target.value})}
          className="input-field"
          placeholder="e.g., Villa Rossi - Kitchen"
          required
          data-testid="project-name-input"
        />
      </div>
      <div>
        <Label className="caption-text mb-2 block">Client / Reference</Label>
        <Input
          value={formData.project_name}
          onChange={(e) => setFormData({...formData, project_name: e.target.value})}
          className="input-field"
          placeholder="e.g., Smith Residence"
          data-testid="project-client-input"
        />
      </div>
      <div>
        <Label className="caption-text mb-2 block">
          <MapPin className="w-3 h-3 inline mr-1" />
          Project City (USA)
        </Label>
        <Popover open={cityOpen} onOpenChange={setCityOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={cityOpen}
              className="input-field w-full justify-between font-normal"
              data-testid="project-city-select"
            >
              {formData.city || "Select city..."}
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-full p-0" align="start">
            <Command>
              <CommandInput 
                placeholder="Search US cities..." 
                value={citySearch}
                onValueChange={setCitySearch}
              />
              <CommandList>
                <CommandEmpty>No city found.</CommandEmpty>
                <CommandGroup>
                  {filteredCities.map((city) => (
                    <CommandItem
                      key={city}
                      value={city}
                      onSelect={() => {
                        setFormData({...formData, city});
                        setCityOpen(false);
                        setCitySearch('');
                      }}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          formData.city === city ? "opacity-100" : "opacity-0"
                        )}
                      />
                      {city}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
      <div>
        <Label className="caption-text mb-2 block">Cover Image URL (optional)</Label>
        <Input
          value={formData.cover_image}
          onChange={(e) => setFormData({...formData, cover_image: e.target.value})}
          className="input-field"
          placeholder="https://..."
          data-testid="project-cover-input"
        />
        <p className="text-xs text-muted-foreground mt-1">
          Leave empty to use the first material's image
        </p>
      </div>
      <div>
        <Label className="caption-text mb-2 block">Notes</Label>
        <Textarea
          value={formData.notes}
          onChange={(e) => setFormData({...formData, notes: e.target.value})}
          className="input-field min-h-[80px]"
          placeholder="Any notes about this project..."
          data-testid="project-notes-input"
        />
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-[#0A0A09]">
      <Header />
      
      <main className="max-w-[1800px] mx-auto px-6 md:px-12 py-12 pt-28">
        {/* Breadcrumb */}
        <Breadcrumb currentPage={t('projects.title')} />
        
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-12">
          <div>
            <p className="caption-text text-muted-foreground mb-2">{t('dashboard.yourProjects')}</p>
            <h1 className="heading-display text-4xl md:text-5xl font-medium" data-testid="projects-title">
              {t('dashboard.designBoards')}
            </h1>
          </div>
          <Dialog open={showCreate} onOpenChange={setShowCreate}>
            <DialogTrigger asChild>
              <Button className="btn-primary mt-6 md:mt-0 inline-flex items-center gap-2" data-testid="create-project-btn">
                <Plus className="w-4 h-4" /> {t('projects.createNew')}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="heading-display text-xl">{t('projects.createNew')}</DialogTitle>
              </DialogHeader>
              <form onSubmit={createProject} className="space-y-4 mt-4">
                <ProjectFormFields />
                <Button type="submit" className="btn-primary w-full" data-testid="submit-project-btn">
                  {t('common.create')}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Projects Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="aspect-[4/3] bg-secondary mb-4"></div>
                <div className="h-5 bg-secondary w-32 mb-2"></div>
                <div className="h-4 bg-secondary w-24"></div>
              </div>
            ))}
          </div>
        ) : projects.length === 0 ? (
          <div className="text-center py-24">
            <FolderKanban className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="heading-display text-xl font-medium mb-2">No projects yet</h3>
            <p className="text-muted-foreground mb-6">Create your first project to start organizing materials for your clients.</p>
            <Button onClick={() => setShowCreate(true)} className="btn-primary" data-testid="empty-create-btn">
              <Plus className="w-4 h-4 mr-2" /> Create Project
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {projects.map((project) => {
              const coverImage = getProjectImage(project);
              return (
                <div key={project.board_id} className="group" data-testid={`project-card-${project.board_id}`}>
                  <Link to={`/projects/${project.board_id}`} className="block">
                    <div className="aspect-[4/3] bg-secondary mb-4 overflow-hidden relative">
                      {coverImage ? (
                        <img 
                          src={coverImage} 
                          alt={project.name}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      ) : project.items?.length > 0 ? (
                        <div className="grid grid-cols-2 gap-1 h-full">
                          {project.items.slice(0, 4).map((item, idx) => (
                            <div key={idx} className="overflow-hidden bg-stone-warm/30">
                              {item.material?.images?.[0] ? (
                                <img 
                                  src={item.material.images[0]} 
                                  alt=""
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <FolderKanban className="w-6 h-6 text-muted-foreground/30" />
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="flex flex-col items-center justify-center h-full bg-gradient-to-br from-stone-warm/20 to-stone-cool/20">
                          <FolderKanban className="w-12 h-12 text-muted-foreground/30 mb-2" />
                          <span className="text-xs text-muted-foreground/50 uppercase tracking-wider">Empty Project</span>
                        </div>
                      )}
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                        <span className="bg-[#b08b5c] text-white px-4 py-2 text-xs uppercase tracking-wider font-medium">
                          View Project
                        </span>
                      </div>
                    </div>
                  </Link>
                  
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="heading-display text-lg font-medium">{project.name}</h3>
                      {project.project_name && (
                        <p className="text-sm text-muted-foreground">{project.project_name}</p>
                      )}
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-xs text-muted-foreground">
                          {project.items?.length || 0} materials
                        </span>
                        {project.city && (
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <MapPin className="w-3 h-3" /> {project.city}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`project-menu-${project.board_id}`}>
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate(`/projects/${project.board_id}`)}>
                          <ArrowRight className="w-4 h-4 mr-2" /> Open
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => openEdit(project)}>
                          <Edit className="w-4 h-4 mr-2" /> Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => deleteProject(project.board_id)}
                          className="text-destructive"
                        >
                          <Trash2 className="w-4 h-4 mr-2" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Edit Dialog */}
      <Dialog open={!!editProject} onOpenChange={() => setEditProject(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="heading-display text-xl">Edit Project</DialogTitle>
          </DialogHeader>
          <form onSubmit={updateProject} className="space-y-4 mt-4">
            <ProjectFormFields isEdit />
            <Button type="submit" className="btn-primary w-full">
              Save Changes
            </Button>
          </form>
        </DialogContent>
      </Dialog>
      
      <Footer />
    </div>
  );
};

export default Projects;
